import { useCallback } from 'react';
import { useDashboard, DrillItem } from '../context/DashboardContext';

export interface DrillDimension {
  level: 'monthly' | 'weekly' | 'daily';
  label: string;
  apiParam: string;
}

/**
 * Manages per-chart drill-down state.
 * drillDown(label, filter) pushes a new level.
 * drillUp(toIndex)  pops back to that index (0 = root).
 */
export function useDrillDown(chartId: string) {
  const { drillState, setDrillState } = useDashboard();
  const path: DrillItem[] = drillState[chartId] || [];

  const drillDown = useCallback((label: string, filter: Record<string, unknown>) => {
    setDrillState((prev) => ({
      ...prev,
      [chartId]: [...(prev[chartId] || []), { label, filter }],
    }));
  }, [chartId, setDrillState]);

  const drillUp = useCallback((toIndex: number) => {
    setDrillState((prev) => ({
      ...prev,
      [chartId]: (prev[chartId] || []).slice(0, toIndex),
    }));
  }, [chartId, setDrillState]);

  const reset = useCallback(() => {
    setDrillState((prev) => ({ ...prev, [chartId]: [] }));
  }, [chartId, setDrillState]);

  /** Merged filter params from every drill level */
  const currentFilter = path.reduce<Record<string, unknown>>(
    (acc, item) => ({ ...acc, ...item.filter }), {}
  );

  return {
    path,
    drillDown,
    drillUp,
    reset,
    isRoot: path.length === 0,
    currentFilter,
    depth: path.length,
  };
}
